import React, { useContext, useState, useEffect } from "react";
import { auth, database } from "../firebase";
import { Alert } from "react-bootstrap";
import firebase from "firebase/app"; // Import Firebase for Google and GitHub auth

const AuthContext = React.createContext();

// Global registry to track all active Firestore listeners
const firestoreListeners = [];

// Helper function to register a new Firestore listener
export function registerFirestoreListener(unsubscribeFunction) {
  firestoreListeners.push(unsubscribeFunction);
  return unsubscribeFunction;
}

// Helper function to unsubscribe from a specific listener
export function unregisterFirestoreListener(unsubscribeFunction) {
  const index = firestoreListeners.indexOf(unsubscribeFunction);
  if (index > -1) {
    firestoreListeners.splice(index, 1);
  }
  return unsubscribeFunction();
}

// Helper function to unsubscribe from all listeners
export function unregisterAllFirestoreListeners() {
  while (firestoreListeners.length > 0) {
    const unsubscribe = firestoreListeners.pop();
    if (typeof unsubscribe === 'function') {
      try {
        unsubscribe();
      } catch (error) {
        console.error("Error unsubscribing from Firestore listener:", error);
      }
    }
  }
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState();
  const [loading, setLoading] = useState(true);
  const [logoutMessage, setLogoutMessage] = useState(""); // State for logout message
  const [isSystemLogout, setIsSystemLogout] = useState(false); // State to track if logout is system-initiated

  async function signup(email, password, firstname, lastname, dob, token) {
    try {
      const cred = await auth.createUserWithEmailAndPassword(email, password);
      await database.users.doc(cred.user.uid).set({
        firstname: firstname,
        lastname: lastname,
        dob: dob,
        accountType: "Individual",
        subscriptiontype: "Basic", // All users should be able to download the free/basic software version
        subscriptionKey: cred.user.uid,
        signupAt: firebase.firestore.FieldValue.serverTimestamp(),
        signupSource: "web-email",
      });
      
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('Email already in use.');
      } else if (error.code === 'auth/weak-password') {
        throw new Error('Password is too weak.');
      } else {
        throw new Error('Failed to create an account.');
      }
    }
  }

  async function login(email, password) {
    try {
      await auth.signInWithEmailAndPassword(email, password);
        return true;  
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        throw new Error('No user found with this email.');
      } else if (error.code === 'auth/wrong-password') {
        throw new Error('Incorrect password.');
      } else {
        throw new Error('Failed to log in.');
      }
    }
  }


  function logout(isSystem = false) {
    if (isSystem) {
      console.log("Auto Logout");
      setLogoutMessage("You have been logged out due to inactivity."); // Set logout message for system logout
    } else {
      console.log("Logout triggered by user");
      setLogoutMessage(""); // Clear logout message for user-initiated logout
    }
    
    // Unsubscribe from all Firestore listeners before signing out
    unregisterAllFirestoreListeners();
    
    return auth.signOut().then(() => {
      console.log("User logged out");
      setCurrentUser(null);
    });
  }

  function resetPassword(email) {
    return auth.sendPasswordResetEmail(email);
  }

  function updatePassword(password) {
    return currentUser.updatePassword(password);
  }

  // --- Custom email action handler helpers ---
  // These back the /auth/action page, which handles the links in Firebase's
  // transactional emails (password reset, email verification, email recovery)
  // on our own domain instead of the default *.firebaseapp.com handler.

  // Inspect an out-of-band action code without consuming it. Returns metadata
  // including the operation type and the associated email.
  function checkActionCode(oobCode) {
    return auth.checkActionCode(oobCode);
  }

  // Password reset: verify the code is valid (returns the account email), then
  // apply the new password.
  function verifyPasswordResetCode(oobCode) {
    return auth.verifyPasswordResetCode(oobCode);
  }

  function confirmPasswordReset(oobCode, newPassword) {
    return auth.confirmPasswordReset(oobCode, newPassword);
  }

  // Email verification / recovery: applies (consumes) the action code.
  function applyActionCode(oobCode) {
    return auth.applyActionCode(oobCode);
  }

  async function signInWithGoogle(options = {}) {
    const provider = new firebase.auth.GoogleAuthProvider();
    if (options.selectAccount) {
      provider.setCustomParameters({ prompt: 'select_account' });
    }
    try {
      const result = await auth.signInWithPopup(provider);
      await handleSocialSignIn(result);
      return true;
    } catch (error) {
      // Same email already registered under another provider → link instead
      // of failing, so the user keeps one account with multiple sign-in methods.
      if (error.code === 'auth/account-exists-with-different-credential') {
        return await linkPendingCredential(error);
      }
      console.error("Google sign in error:", error);
      throw new Error(describeSocialSignInError(error, 'Google'));
    }
  }

  // Microsoft Entra ID (Azure AD) sign-in. Used for enterprise SSO.
  // `tenantId` (optional) pins the flow to a single Entra tenant (e.g. a
  // customer's directory). When omitted, the Firebase provider default applies.
  async function signInWithMicrosoft(tenantId) {
    const provider = new firebase.auth.OAuthProvider('microsoft.com');
    // Request the standard OIDC scopes so we get email + profile back.
    provider.addScope('email');
    provider.addScope('profile');
    const customParams = { prompt: 'select_account' };
    if (tenantId) {
      customParams.tenant = tenantId;
    }
    provider.setCustomParameters(customParams);

    try {
      const result = await auth.signInWithPopup(provider);
      await handleSocialSignIn(result, 'microsoft');
      return true;
    } catch (error) {
      // Same email already registered under another provider (e.g. Google) →
      // link the Microsoft credential to that existing account.
      if (error.code === 'auth/account-exists-with-different-credential') {
        return await linkPendingCredential(error);
      }
      console.error("Microsoft sign in error:", error);
      throw new Error(describeSocialSignInError(error, 'Microsoft'));
    }
  }

  // Map Firebase popup errors to clear, actionable messages. We intentionally do
  // NOT fall back to signInWithRedirect: redirect relies on storage on the
  // *.firebaseapp.com auth-handler origin, which modern browsers partition
  // (and localhost blocks entirely), producing the confusing "missing initial
  // state" error. Popups avoid that, so popup is the single source of truth.
  function describeSocialSignInError(error, providerLabel) {
    switch (error?.code) {
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request':
        return 'Sign-in was cancelled.';
      case 'auth/popup-blocked':
        return `Your browser blocked the ${providerLabel} sign-in popup. Please allow popups for this site and try again.`;
      default:
        return error?.message || `Failed to sign in with ${providerLabel}.`;
    }
  }

  // When a user signs in with a NEW provider but their email already exists
  // under a DIFFERENT provider, Firebase (with the default one-account-per-email
  // setting) throws 'auth/account-exists-with-different-credential'. Rather than
  // blocking the user or creating a duplicate account, we verify ownership by
  // signing in with the provider already on file, then link the new credential.
  // Result: one account, multiple sign-in methods (the multi-provider icons).
  async function linkPendingCredential(error) {
    const pendingCred = error.credential;
    const email = error.email;
    if (!pendingCred || !email) {
      throw new Error('Unable to link this sign-in method. Please use your original sign-in provider.');
    }

    const methods = await auth.fetchSignInMethodsForEmail(email);

    let result;
    if (methods.includes('google.com')) {
      const existing = new firebase.auth.GoogleAuthProvider();
      existing.setCustomParameters({ login_hint: email });
      result = await auth.signInWithPopup(existing);
    } else if (methods.includes('microsoft.com')) {
      const existing = new firebase.auth.OAuthProvider('microsoft.com');
      existing.setCustomParameters({ login_hint: email });
      result = await auth.signInWithPopup(existing);
    } else {
      // Only password (or an unsupported method) is on file — cannot auto-link
      // through a popup. Tell the user which method to use first.
      const human = methods.includes('password') ? 'email and password' : (methods[0] || 'your original method');
      throw new Error(`This email is already registered. Please sign in with ${human} first, then add the new provider.`);
    }

    await result.user.linkWithCredential(pendingCred);
    await handleSocialSignIn(result, methods.includes('microsoft.com') ? 'microsoft' : 'google');
    return true;
  }

  // Helper function to handle social sign-in result
  async function handleSocialSignIn(result, providerSource = 'google') {
    
    // Check if user exists in our database
    const userDoc = await database.users.doc(result.user.uid).get();

    if (!userDoc.exists) {
      // Create new user document with data from social provider
      await database.users.doc(result.user.uid).set({
        firstname: result.user.displayName ? result.user.displayName.split(' ')[0] : 'User',
        lastname: result.user.displayName ? result.user.displayName.split(' ').slice(1).join(' ') : '',
        email: result.user.email || `${result.user.uid}@placeholder.com`,
        accountType: "Individual",
        subscriptiontype: "Basic",
        subscriptionKey: result.user.uid,
        signupAt: firebase.firestore.FieldValue.serverTimestamp(),
        signupSource: providerSource === 'microsoft' ? "web-microsoft" : "web-google",
      });
    }
  }



  useEffect(() => {
    // Handle the redirect sign-in result
    auth.getRedirectResult().then(async (result) => {
      if (result?.user) {
        try {
          const providerId = result.additionalUserInfo?.providerId || '';
          const providerSource = providerId.includes('microsoft') ? 'microsoft' : 'google';
          await handleSocialSignIn(result, providerSource);
        } catch (error) {
          console.error("Error handling redirect result:", error);
        }
      }
    }).catch((error) => {
      console.error("Redirect result error:", error);
    });

    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
          setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    }, error => {
      console.error('Auth state change error:', error);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    let timer;
    if (logoutMessage) {
      timer = setTimeout(() => {
        setLogoutMessage("");
        setIsSystemLogout(false);
      }, 5000); // Hide alert after 5 seconds
    }
    return () => clearTimeout(timer); // Cleanup the timeout on component unmount or when logoutMessage changes
  }, [logoutMessage]);

  const value = {
    currentUser,
    login,
    signup,
    logout: () => logout(false), // Pass false for user-initiated logout
    resetPassword,
    updatePassword,
    checkActionCode,
    verifyPasswordResetCode,
    confirmPasswordReset,
    applyActionCode,
    signInWithGoogle,
    signInWithMicrosoft,
    registerListener: registerFirestoreListener, // Expose the listener registration function
    unregisterListener: unregisterFirestoreListener, // Expose the listener unregistration function
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && (
        <>
          {isSystemLogout && logoutMessage && <Alert variant="info">{logoutMessage}</Alert>} {/* Display logout message */}
          {children}
        </>
      )}
    </AuthContext.Provider>
  );
}